import React, { useState } from 'react';
import { Modal, Checkbox, Input, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { createSubscriptionFeedback } from '@/api/subtitles';
import type { CancellationReasonModalProps } from './types';

const { TextArea } = Input;

// 取消理由选项映射
const CANCELLATION_REASONS: Array<{
  key: API.CancellationReasonType;
  labelKey: string;
}> = [
    { key: 'TOO_EXPENSIVE', labelKey: 'account.cancellationReason.tooExpensive' },
    { key: 'NEED_MORE_FEATURES', labelKey: 'account.cancellationReason.needMoreFeatures' },
    { key: 'FOUND_ALTERNATIVE', labelKey: 'account.cancellationReason.foundAlternative' },
    { key: 'NO_NEED_IT', labelKey: 'account.cancellationReason.noNeedIt' },
    { key: 'LESS_THAN_EXPECTED', labelKey: 'account.cancellationReason.lessThanExpected' },
    { key: 'POOR_USABILITY', labelKey: 'account.cancellationReason.poorUsability' },
    { key: 'LOW_QUALITY', labelKey: 'account.cancellationReason.lowQuality' },
    { key: 'BUSINESS', labelKey: 'account.cancellationReason.business' },
    { key: 'OTHER', labelKey: 'account.cancellationReason.other' },
  ];

const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({
  open,
  onClose,
  onSuccess,
  userEmail,
}) => {
  const { t } = useTranslation();
  const [selectedReasons, setSelectedReasons] = useState<API.CancellationReasonType[]>([]);
  const [additionalComments, setAdditionalComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState(false);

  // 重置表单
  const resetForm = () => {
    setSelectedReasons([]);
    setAdditionalComments('');
    setValidationError(false);
  };

  // 处理关闭弹窗
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // 处理复选框变化
  const handleCheckboxChange = (checkedValues: API.CancellationReasonType[]) => {
    setSelectedReasons(checkedValues);
    if (checkedValues.length > 0) {
      setValidationError(false);
    }
  };

  // 处理提交
  const handleSubmit = async () => {
    // 表单验证
    if (selectedReasons.length === 0) {
      setValidationError(true);
      message.error(t('account.cancellationReasonModal.validationError'));
      return;
    }

    setLoading(true);

    try {
      // 调用 API 提交反馈
      await createSubscriptionFeedback({
        email: userEmail,
        platform: 'web',
        reason_content: additionalComments || '',
        reason_types: selectedReasons,
      });

      // 提交成功
      message.success(t('account.cancellationReasonModal.submitSuccess'));
      resetForm();
      onSuccess();
    } catch (error) {
      // 提交失败
      console.error('Failed to submit cancellation feedback:', error);
      message.error(t('account.cancellationReasonModal.submitError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('account.cancellationReasonModal.title')}
      onCancel={handleClose}
      width={560}
      footer={[
        <Button key="close" onClick={handleClose} disabled={loading}>
          {t('account.cancellationReasonModal.close')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          style={{
            backgroundColor: '#7c3aed',
            borderColor: '#7c3aed',
          }}
        >
          {t('account.cancellationReasonModal.submit')}
        </Button>,
      ]}
      centered
      maskClosable={!loading}
      closable={!loading}
    >
      <div>
        {/* 描述文字 */}
        <p style={{ marginBottom: 6, color: '#666', fontSize: 14 }}>
          {t('account.cancellationReasonModal.description')}
        </p>

        {/* 取消理由复选框列表 */}
        <div style={{ marginBottom: 24 }}>
          <Checkbox.Group
            value={selectedReasons}
            onChange={handleCheckboxChange as any}
            disabled={loading}
            style={{ width: '100%' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
              {CANCELLATION_REASONS.map((reason) => (
                <div
                  key={reason.key}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    transition: 'background-color 0.2s',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                  className="cancellation-reason-option"
                >
                  <Checkbox value={reason.key} style={{ width: '100%' }}>
                    <span style={{ fontSize: 14 }}>{t(reason.labelKey)}</span>
                  </Checkbox>
                </div>
              ))}
            </div>
          </Checkbox.Group>

          {/* 验证错误提示 */}
          {validationError && (
            <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: 13 }}>
              {t('account.cancellationReasonModal.validationError')}
            </div>
          )}
        </div>

        {/* 补充说明输入框 */}
        <div style={{ marginBottom: 40 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontSize: 14,
              fontWeight: 500,
              color: '#333',
            }}
          >
            {t('account.cancellationReasonModal.additionalComments')}
          </label>
          <TextArea
            value={additionalComments}
            onChange={(e) => setAdditionalComments(e.target.value)}
            placeholder={t('account.cancellationReasonModal.placeholder')}
            disabled={loading}
            rows={4}
            maxLength={500}
            showCount
            style={{
              resize: 'none',
            }}
          />
        </div>
      </div>

      {/* 添加样式 */}
      <style>{`
        .cancellation-reason-option:hover {
          background-color: ${loading ? 'transparent' : '#faf5ff'};
        }
        .ant-checkbox-wrapper {
          width: 100%;
        }
        .ant-checkbox-wrapper:hover .ant-checkbox-inner {
          border-color: #7c3aed;
        }
        .ant-checkbox-checked .ant-checkbox-inner {
          background-color: #7c3aed;
          border-color: #7c3aed;
        }
        .ant-checkbox-checked::after {
          border-color: #7c3aed;
        }
      `}</style>
    </Modal>
  );
};

export default CancellationReasonModal;
